// MessageriePanel.jsx
import { AnimatePresence, motion } from 'framer-motion';
import {
    AlertCircle, Archive, ArrowLeft, Bell, CheckCheck,
    Edit3, Inbox, MailOpen, Search, Send, Shield,
    User, Wifi, WifiOff, X, Zap,
} from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMessagerie } from '../hooks/useMessagerie';

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (date) => {
  if (!date) return '—';
  const d = Date.now() - new Date(date).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1)  return "à l'instant";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const j = Math.floor(h / 24);
  if (j < 7)  return `${j}j`;
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

const typeConf = (type, role) => {
  if (role === 'system' || type === 'system' || type === 'notification')
    return { label: 'SYS',  dot: '#818cf8', bd: 'rgba(99,102,241,.25)',  bg: 'rgba(99,102,241,.06)'  };
  if (type === 'credentials')
    return { label: 'CRED', dot: '#fbbf24', bd: 'rgba(245,158,11,.25)',  bg: 'rgba(245,158,11,.06)'  };
  if (type === 'alert')
    return { label: 'ALRT', dot: '#f87171', bd: 'rgba(239,68,68,.25)',   bg: 'rgba(239,68,68,.06)'   };
  return   { label: 'MSG',  dot: '#fb923c', bd: 'rgba(249,115,22,.25)',  bg: 'rgba(249,115,22,.06)'  };
};

const unread = (msg, uid) => {
  if (msg.status === 'read') return false;
  const r = msg.to?.find(r => (r.userId?._id || r.userId)?.toString() === uid?.toString());
  return !r?.readAt;
};

// ─── shared input style ───────────────────────────────────────────────────────

const inp = (dark) => ({
  background:  dark ? 'rgba(0,0,0,.45)' : 'rgba(0,0,0,.02)',
  border:      `1px solid ${dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.1)'}`,
  color:       dark ? '#d4d4d8' : '#3f3f46',
  outline:     'none',
  transition:  'border-color .15s',
});
const focusOn  = e => { e.target.style.borderColor = 'rgba(249,115,22,.45)'; };
const focusOff = (dark) => e => { e.target.style.borderColor = dark ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.1)'; };

// ─────────────────────────────────────────────────────────────────────────────
// MessageRow
// ─────────────────────────────────────────────────────────────────────────────

const MessageRow = ({ msg, uid, onClick, onArchive, dark, sent = false }) => {
  const u  = !sent && unread(msg, uid);
  const tc = typeConf(msg.messageType, msg.from?.type);
  const from = sent ? (msg.to?.map(r => r.name).join(', ') || '—') : (msg.from?.name || 'Système');

  return (
    <motion.div
      layout initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }}
      transition={{ duration: .13 }}
      onClick={() => onClick(msg)}
      className="group relative flex items-start gap-3 px-4 py-3.5 cursor-pointer"
      style={{ borderBottom: `1px solid ${dark ? 'rgba(255,255,255,.04)' : 'rgba(0,0,0,.05)'}`,
               background: u ? (dark ? 'rgba(249,115,22,.04)' : 'rgba(249,115,22,.03)') : 'transparent' }}
      onMouseEnter={e => e.currentTarget.style.background = dark ? 'rgba(255,255,255,.025)' : 'rgba(0,0,0,.02)'}
      onMouseLeave={e => e.currentTarget.style.background = u ? (dark ? 'rgba(249,115,22,.04)' : 'rgba(249,115,22,.03)') : 'transparent'}
    >
      {u && <div className="absolute left-0 top-0 bottom-0 w-0.5" style={{ background: tc.dot }} />}

      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center"
        style={{ border: `1px solid ${tc.bd}`, background: tc.bg }}>
        {sent ? <Send size={12} style={{ color: tc.dot }} />
          : msg.from?.type === 'system' ? <Zap    size={12} style={{ color: tc.dot }} />
          : msg.from?.type === 'admin'  ? <Shield size={12} style={{ color: tc.dot }} />
          :                               <User   size={12} style={{ color: tc.dot }} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[7px] font-mono font-bold px-1 py-px flex-shrink-0"
              style={{ border: `1px solid ${tc.bd}`, color: tc.dot, background: tc.bg }}>{tc.label}</span>
            <span className={`text-[10px] font-bold truncate ${u ? (dark?'text-white':'text-zinc-900') : (dark?'text-zinc-400':'text-zinc-600')}`}>
              {sent ? `→ ${from}` : from}
            </span>
          </div>
          <span className={`text-[8px] font-mono flex-shrink-0 ${dark?'text-zinc-600':'text-zinc-400'}`}>{fmt(msg.sentAt)}</span>
        </div>
        <p className={`text-[11px] truncate ${u ? (dark?'text-zinc-100 font-semibold':'text-zinc-800 font-semibold') : (dark?'text-zinc-400':'text-zinc-600')}`}>
          {msg.subject}
        </p>
        <p className={`text-[9px] truncate mt-px ${dark?'text-zinc-600':'text-zinc-400'}`}>
          {msg.body?.replace(/\*\*/g,'').substring(0,72)}…
        </p>
      </div>

      {!sent && onArchive && (
        <button onClick={e => { e.stopPropagation(); onArchive(msg._id); }}
          className="opacity-0 group-hover:opacity-100 flex-shrink-0 p-1 transition-opacity"
          style={{ color: dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)' }}
          onMouseEnter={e => e.currentTarget.style.color='#f97316'}
          onMouseLeave={e => e.currentTarget.style.color=dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)'}
        ><Archive size={11} /></button>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MessageDetail
// ─────────────────────────────────────────────────────────────────────────────

const MessageDetail = ({ msg, uid, onBack, onReply, onArchive, dark, sent }) => {
  const [open,    setOpen]   = useState(false);
  const [body,    setBody]   = useState('');
  const [sending, setSend]   = useState(false);
  const taRef = useRef(null);
  const tc = typeConf(msg.messageType, msg.from?.type);
  const canReply = !sent && msg.from?.type !== 'system' && msg.messageType === 'conversation';
  const div = dark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.05)';

  const send = async () => {
    if (!body.trim() || sending) return;
    setSend(true); await onReply(msg._id, body); setBody(''); setOpen(false); setSend(false);
  };

  return (
    <div className="flex flex-col" style={{ height: '100%' }}>
      {/* header */}
      <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${div}` }}>
        <button onClick={onBack} className="p-1.5 flex-shrink-0"
          style={{ border:`1px solid ${dark?'rgba(255,255,255,.08)':'rgba(0,0,0,.08)'}`, color:dark?'rgba(255,255,255,.4)':'rgba(0,0,0,.4)' }}
          onMouseEnter={e=>{e.currentTarget.style.color='#f97316';e.currentTarget.style.borderColor='rgba(249,115,22,.4)';}}
          onMouseLeave={e=>{e.currentTarget.style.color=dark?'rgba(255,255,255,.4)':'rgba(0,0,0,.4)';e.currentTarget.style.borderColor=dark?'rgba(255,255,255,.08)':'rgba(0,0,0,.08)';}}
        ><ArrowLeft size={13}/></button>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-black uppercase tracking-tight truncate ${dark?'text-white':'text-zinc-900'}`}>{msg.subject}</p>
          <div className="flex items-center gap-1.5 mt-px">
            <span className="text-[7px] font-mono px-1 py-px" style={{ border:`1px solid ${tc.bd}`,color:tc.dot,background:tc.bg }}>{tc.label}</span>
            <span className={`text-[8px] font-mono ${dark?'text-zinc-600':'text-zinc-400'}`}>{msg.from?.name} · {fmt(msg.sentAt)}</span>
          </div>
        </div>
        {!sent && <button onClick={()=>{onArchive(msg._id);onBack();}} className="p-1.5 flex-shrink-0"
          style={{ color:dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)' }}
          onMouseEnter={e=>e.currentTarget.style.color='#f97316'}
          onMouseLeave={e=>e.currentTarget.style.color=dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)'}
        ><Archive size={13}/></button>}
      </div>

      {/* meta */}
      <div className="flex items-start gap-3 px-4 py-3 flex-shrink-0"
        style={{ borderBottom:`1px solid ${div}`, background:dark?'rgba(255,255,255,.015)':'rgba(0,0,0,.015)' }}>
        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0" style={{ border:`1px solid ${tc.bd}`,background:tc.bg }}>
          {msg.from?.type==='system'?<Zap size={16} style={{color:tc.dot}}/>:msg.from?.type==='admin'?<Shield size={16} style={{color:tc.dot}}/>:<User size={16} style={{color:tc.dot}}/>}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-bold ${dark?'text-zinc-200':'text-zinc-800'}`}>{msg.from?.name}</p>
          <p className={`text-[8px] font-mono ${dark?'text-zinc-600':'text-zinc-400'}`}>
            {msg.from?.role?.toUpperCase()} · {new Date(msg.sentAt).toLocaleDateString('fr-FR',{day:'2-digit',month:'long',year:'numeric',hour:'2-digit',minute:'2-digit'})}
          </p>
          {msg.to?.length>0 && <p className={`text-[8px] font-mono mt-px ${dark?'text-zinc-600':'text-zinc-400'}`}>À : {msg.to.map(r=>r.name).join(', ')}</p>}
        </div>
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto px-4 py-5" style={{ scrollbarWidth:'thin' }}>
        <div className={`text-[12px] leading-7 whitespace-pre-wrap ${dark?'text-zinc-300':'text-zinc-700'}`}>
          {msg.body?.split(/(\*\*[^*]+\*\*)/).map((p,i)=>
            /^\*\*.*\*\*$/.test(p)
              ? <strong key={i} className={dark?'text-white':'text-zinc-900'}>{p.slice(2,-2)}</strong>
              : <span key={i}>{p}</span>
          )}
        </div>
      </div>

      {/* reply */}
      {canReply && (
        <div className="flex-shrink-0 px-4 py-3" style={{ borderTop:`1px solid ${div}` }}>
          <AnimatePresence mode="wait">
            {open ? (
              <motion.div key="o" initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}} className="space-y-2 overflow-hidden">
                <p className={`text-[8px] font-mono uppercase tracking-widest ${dark?'text-zinc-600':'text-zinc-400'}`}>Répondre à {msg.from?.name}</p>
                <textarea ref={taRef} value={body} onChange={e=>setBody(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter'&&(e.ctrlKey||e.metaKey))send();}}
                  placeholder="Votre réponse… (Ctrl+↵)" rows={4}
                  className="w-full text-[11px] font-mono resize-none px-3 py-2.5"
                  style={inp(dark)} onFocus={focusOn} onBlur={focusOff(dark)}
                />
                <div className="flex gap-2">
                  <button onClick={send} disabled={!body.trim()||sending}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-mono font-bold uppercase tracking-widest"
                    style={{ background:body.trim()&&!sending?'#f97316':(dark?'rgba(255,255,255,.05)':'rgba(0,0,0,.05)'), color:body.trim()&&!sending?'white':(dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)'), cursor:body.trim()&&!sending?'pointer':'not-allowed' }}
                  ><Send size={10}/> {sending?'Envoi…':'Envoyer'}</button>
                  <button onClick={()=>{setOpen(false);setBody('');}}
                    className="px-3 py-1.5 text-[9px] font-mono uppercase tracking-widest"
                    style={{ border:`1px solid ${dark?'rgba(255,255,255,.08)':'rgba(0,0,0,.08)'}`, color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)' }}
                  >Annuler</button>
                </div>
              </motion.div>
            ) : (
              <motion.button key="c" initial={{opacity:0}} animate={{opacity:1}}
                onClick={()=>{setOpen(true);setTimeout(()=>taRef.current?.focus(),80);}}
                className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-widest"
                style={{ color:dark?'rgba(255,255,255,.25)':'rgba(0,0,0,.3)' }}
                onMouseEnter={e=>e.currentTarget.style.color='#f97316'}
                onMouseLeave={e=>e.currentTarget.style.color=dark?'rgba(255,255,255,.25)':'rgba(0,0,0,.3)'}
              ><Send size={11}/> Répondre</motion.button>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Composer  ← vue complète, pas un overlay
// ─────────────────────────────────────────────────────────────────────────────

const Composer = ({ onSend, onClose, dark }) => {
  const [to,      setTo]      = useState('');
  const [subject, setSubject] = useState('');
  const [body,    setBody]    = useState('');
  const [prio,    setPrio]    = useState('normal');
  const [sending, setSending] = useState(false);
  const [done,    setDone]    = useState(false);

  const ok = to.trim() && subject.trim() && body.trim() && !sending;
  const div = dark ? 'rgba(255,255,255,.05)' : 'rgba(0,0,0,.05)';

  const submit = async () => {
    if (!ok) return;
    setSending(true);
    await onSend(to.trim(), subject.trim(), body.trim(), { priority: prio });
    setDone(true);
    setTimeout(onClose, 1400);
  };

  return (
    /* flex-1 vient du parent dans MessageriePanel → occupe toute la hauteur */
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                    padding:'12px 16px', flexShrink:0, borderBottom:`1px solid ${div}` }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <Edit3 size={13} color="#f97316" />
          <span style={{ fontSize:11, fontWeight:900, textTransform:'uppercase', letterSpacing:'0.15em', color: dark?'#fff':'#18181b' }}>
            Nouveau message
          </span>
        </div>
        <button onClick={onClose}
          style={{ color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)', cursor:'pointer', background:'none', border:'none', padding:4, lineHeight:0 }}
          onMouseEnter={e=>e.currentTarget.style.color=dark?'white':'black'}
          onMouseLeave={e=>e.currentTarget.style.color=dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)'}
        ><X size={14}/></button>
      </div>

      {done ? (
        /* ── Confirmation ── */
        <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:16 }}>
          <motion.div initial={{scale:.8,opacity:0}} animate={{scale:1,opacity:1}}
            style={{ width:56,height:56,display:'flex',alignItems:'center',justifyContent:'center',
                     background:'rgba(34,197,94,.1)',border:'1px solid rgba(34,197,94,.25)' }}>
            <CheckCheck size={26} color="#4ade80" />
          </motion.div>
          <div style={{ textAlign:'center' }}>
            <p style={{ fontSize:11,fontWeight:900,textTransform:'uppercase',letterSpacing:'0.15em', color:dark?'#fff':'#18181b' }}>Message envoyé</p>
            <p style={{ fontSize:8,fontFamily:'monospace',marginTop:4, color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)' }}>Transmission réussie</p>
          </div>
        </div>
      ) : (
        <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* ── Champs méta ── */}
          <div style={{ flexShrink:0, padding:'12px 16px 0', borderBottom:`1px solid ${div}` }}>

            {/* À */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
              <label style={{ fontSize:8,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.12em',
                              width:32,textAlign:'right',flexShrink:0, color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.35)' }}>À</label>
              <input value={to} onChange={e=>setTo(e.target.value)}
                placeholder="ID utilisateur ou email"
                style={{ ...inp(dark), flex:1, fontSize:11, fontFamily:'monospace', padding:'6px 10px', borderRadius:0 }}
                onFocus={focusOn} onBlur={focusOff(dark)}
              />
            </div>

            {/* Objet */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8 }}>
              <label style={{ fontSize:8,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.12em',
                              width:32,textAlign:'right',flexShrink:0, color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.35)' }}>Objet</label>
              <input value={subject} onChange={e=>setSubject(e.target.value)}
                placeholder="Sujet"
                style={{ ...inp(dark), flex:1, fontSize:11, fontFamily:'monospace', padding:'6px 10px', borderRadius:0 }}
                onFocus={focusOn} onBlur={focusOff(dark)}
              />
            </div>

            {/* Priorité */}
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
              <label style={{ fontSize:8,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.12em',
                              width:32,textAlign:'right',flexShrink:0, color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.35)' }}>Prio</label>
              <div style={{ display:'flex', gap:6 }}>
                {[
                  { k:'low',    c:null },
                  { k:'normal', c:null },
                  { k:'high',   c:'#fbbf24' },
                  { k:'urgent', c:'#f87171' },
                ].map(({ k, c }) => {
                  const active = prio === k;
                  const col = c || '#f97316';
                  return (
                    <button key={k} onClick={() => setPrio(k)}
                      style={{
                        fontSize:7, fontFamily:'monospace', textTransform:'uppercase', letterSpacing:'0.1em',
                        padding:'3px 8px', cursor:'pointer',
                        border: `1px solid ${active ? col : (dark?'rgba(255,255,255,.07)':'rgba(0,0,0,.08)')}`,
                        background: active ? `${col}18` : 'transparent',
                        color: active ? col : (dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)'),
                      }}
                    >{k}</button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Corps + bouton envoi ── */}
          <div style={{ flex:1, display:'flex', flexDirection:'column', padding:'12px 16px', gap:10, overflow:'hidden' }}>
            <textarea
              value={body} onChange={e=>setBody(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'&&(e.ctrlKey||e.metaKey))submit();}}
              placeholder="Rédigez votre message…"
              style={{ ...inp(dark), flex:1, minHeight:0, fontSize:12, fontFamily:'monospace',
                       lineHeight:1.7, padding:'10px 12px', resize:'none', borderRadius:0 }}
              onFocus={focusOn} onBlur={focusOff(dark)}
            />

            <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
              <button onClick={submit} disabled={!ok}
                style={{
                  display:'flex', alignItems:'center', gap:8,
                  padding:'8px 18px', fontSize:10, fontFamily:'monospace',
                  fontWeight:900, textTransform:'uppercase', letterSpacing:'0.15em',
                  border:'none', cursor: ok ? 'pointer' : 'not-allowed',
                  background: ok ? '#f97316' : (dark?'rgba(255,255,255,.05)':'rgba(0,0,0,.05)'),
                  color:      ok ? 'white'   : (dark?'rgba(255,255,255,.15)':'rgba(0,0,0,.15)'),
                  transition: 'background .15s',
                }}
                onMouseEnter={e=>{ if(ok) e.currentTarget.style.background='#ea6c00'; }}
                onMouseLeave={e=>{ if(ok) e.currentTarget.style.background='#f97316'; }}
              >
                <Send size={12} /> {sending ? 'Envoi…' : 'Envoyer'}
              </button>
              <span style={{ marginLeft:'auto', fontSize:7, fontFamily:'monospace', color:dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)' }}>
                Ctrl+↵
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MessageriePanel
// ─────────────────────────────────────────────────────────────────────────────

export const MessageriePanel = ({
  isOpen, onClose, darkMode: dark,
  userId,
  legacyNotifications = [],
  onLegacyRead, onLegacyReadAll,
  legacyUnreadCount = 0,
}) => {
  const [tab,  setTab]  = useState('inbox');   // inbox | sent | archived | notifications
  const [view, setView] = useState('list');     // list | detail | compose
  const [sel,  setSel]  = useState(null);
  const [q,    setQ]    = useState('');

  const {
    connected, inbox, sent, unreadCount, loading, error,
    markAsRead, archiveMessage, replyToMessage, sendMessage,
    loadSent, loadArchived,
  } = useMessagerie({ userId, enabled: isOpen });

  const totalUnread = unreadCount + legacyUnreadCount;

  const fInbox = useMemo(() => {
    if (!q.trim()) return inbox;
    const s = q.toLowerCase();
    return inbox.filter(m => m.subject?.toLowerCase().includes(s) || m.from?.name?.toLowerCase().includes(s) || m.body?.toLowerCase().includes(s));
  }, [inbox, q]);

  const fSent = useMemo(() => {
    if (!q.trim()) return sent;
    const s = q.toLowerCase();
    return sent.filter(m => m.subject?.toLowerCase().includes(s) || m.body?.toLowerCase().includes(s));
  }, [sent, q]);

  const openMsg = useCallback((msg) => {
    setSel(msg); setView('detail');
    if (unread(msg, userId)) markAsRead(msg._id);
  }, [markAsRead, userId]);

  const back = useCallback(() => { setSel(null); setView('list'); }, []);

  const archive = useCallback((id) => {
    archiveMessage(id);
    if (sel?._id === id) back();
  }, [archiveMessage, sel, back]);

  const changeTab = useCallback((t) => {
    if (t === 'compose') { setView('compose'); return; }  // ← interception
    setTab(t); setView('list'); setSel(null); setQ('');
    if (t === 'sent')     loadSent();
    if (t === 'archived') loadArchived();
    }, [loadSent, loadArchived]);

  const doSend = useCallback((to, subject, body, meta) => {
    sendMessage(to, subject, body, meta);
    return new Promise(r => setTimeout(r, 500));
  }, [sendMessage]);

  // styles
  const bg  = dark ? 'rgba(6,6,14,.98)'     : 'rgba(255,255,255,.98)';
  const brd = dark ? 'rgba(249,115,22,.12)' : 'rgba(249,115,22,.18)';
  const div = dark ? 'rgba(255,255,255,.05)': 'rgba(0,0,0,.05)';

  const TABS = [
  { key:'inbox',         icon:<Inbox   size={11}/>, label:'Inbox',    badge:unreadCount },
  { key:'sent',          icon:<Send    size={11}/>, label:'Envoyés',  badge:0 },
  { key:'archived',      icon:<Archive size={11}/>, label:'Archivés', badge:0 },
  { key:'notifications', icon:<Bell    size={11}/>, label:'Notifs',   badge:legacyUnreadCount },
  { key:'compose',       icon:<Edit3   size={11}/>, label:'Nouveau',  badge:0, isAction:true },
];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background:'rgba(0,0,0,.6)', backdropFilter:'blur(4px)' }}
          />

          <motion.div
            initial={{ x:440, opacity:0 }} animate={{ x:0, opacity:1 }} exit={{ x:440, opacity:0 }}
            transition={{ type:'spring', damping:30, stiffness:260 }}
            className="fixed right-0 top-0 z-50 shadow-2xl"
            style={{ width:420, height:'100%', background:bg, borderLeft:`1px solid ${brd}`,
                     backdropFilter:'blur(20px)', display:'flex', flexDirection:'column' }}
          >

            {/* ════════════════════════════════════════════════════
                COMPOSER — remplace tout le contenu du panneau
            ════════════════════════════════════════════════════ */}
            {view === 'compose' ? (
              <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minHeight:0 }}>
                <Composer
                  onSend={doSend}
                  onClose={() => setView('list')}
                  dark={dark}
                />
              </div>

            ) : (
            /* ════════════════════════════════════════════════════
               HEADER + BODY + FOOTER (mode liste / détail)
            ════════════════════════════════════════════════════ */
            <>
              {/* Header */}
              <div style={{ flexShrink:0, padding:'16px 16px 0', borderBottom:`1px solid ${div}` }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                  <div>
                    <span style={{ fontSize:11, fontWeight:900, textTransform:'uppercase', letterSpacing:'0.2em', color:dark?'#fff':'#18181b' }}>
                      Messagerie
                    </span>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
                      {connected
                        ? <><Wifi size={8} color="#22c55e"/><span style={{ fontSize:7,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.1em',color:'rgba(34,197,94,.6)' }}>Live</span></>
                        : <><WifiOff size={8} color="#f87171"/><span style={{ fontSize:7,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.1em',color:'rgba(248,113,113,.6)' }}>Reconnexion…</span></>
                      }
                      {totalUnread > 0 && <span style={{ fontSize:7,fontFamily:'monospace',color:'#f97316',marginLeft:4 }}>{totalUnread} non lu{totalUnread>1?'s':''}</span>}
                    </div>
                  </div>

                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    

                    {unreadCount > 0 && tab === 'inbox' && (
                      <button title="Tout marquer comme lu"
                        onClick={() => inbox.filter(m=>unread(m,userId)).forEach(m=>markAsRead(m._id))}
                        style={{ padding:6, color:dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)', cursor:'pointer', background:'none', border:'none', lineHeight:0 }}
                        onMouseEnter={e=>e.currentTarget.style.color='#f97316'}
                        onMouseLeave={e=>e.currentTarget.style.color=dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)'}
                      ><CheckCheck size={13}/></button>
                    )}

                    <button onClick={onClose}
                      style={{ padding:6, color:dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)', cursor:'pointer', background:'none', border:'none', lineHeight:0 }}
                      onMouseEnter={e=>e.currentTarget.style.color=dark?'white':'black'}
                      onMouseLeave={e=>e.currentTarget.style.color=dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)'}
                    ><X size={14}/></button>
                  </div>
                </div>

                {/* Recherche (seulement en liste) */}
                {view === 'list' && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12,
                                padding:'6px 10px', background:dark?'rgba(0,0,0,.3)':'rgba(0,0,0,.02)',
                                border:`1px solid ${dark?'rgba(255,255,255,.07)':'rgba(0,0,0,.08)'}` }}>
                    <Search size={11} style={{ color:dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)', flexShrink:0 }} />
                    <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Rechercher…"
                      style={{ flex:1, fontSize:10, fontFamily:'monospace', background:'transparent', border:'none', outline:'none', color:dark?'#a1a1aa':'#52525b' }}
                    />
                    {q && <button onClick={()=>setQ('')} style={{ color:dark?'rgba(255,255,255,.2)':'rgba(0,0,0,.2)', background:'none', border:'none', cursor:'pointer', lineHeight:0 }}><X size={10}/></button>}
                  </div>
                )}

                {/* Tabs */}
                <div style={{ display:'flex', marginBottom:-1 }}>
                  {TABS.map(t => (
                    <button key={t.key} onClick={() => changeTab(t.key)}
                        style={{
                        display:'flex', alignItems:'center', gap:4,
                        padding:'8px 12px', fontSize:8, fontFamily:'monospace',
                        textTransform:'uppercase', letterSpacing:'0.1em', cursor:'pointer',
                        background: t.isAction
                            ? (view === 'compose' ? 'rgba(249,115,22,.15)' : 'none')
                            : 'none',
                        borderTop:'none', borderLeft:'none', borderRight:'none',
                        borderBottom: `2px solid ${
                            t.isAction
                            ? (view === 'compose' ? '#f97316' : 'rgba(249,115,22,.3)')
                            : (tab===t.key ? '#f97316' : 'transparent')
                        }`,
                        color: t.isAction
                            ? '#f97316'
                            : (tab===t.key ? '#f97316' : (dark?'rgba(255,255,255,.3)':'rgba(0,0,0,.3)')),
                        transition: 'color .15s, border-color .15s',
                        marginLeft: t.isAction ? 'auto' : 0,  // pousse "Nouveau" à droite
                        }}
                    >
                        {t.icon} {t.label}
                        {t.badge > 0 && (
                        <span style={{ minWidth:14, height:14, borderRadius:9999, background:'#f97316',
                                        display:'flex', alignItems:'center', justifyContent:'center',
                                        fontSize:6, fontWeight:900, color:'white', padding:'0 2px' }}>
                            {t.badge > 9 ? '9+' : t.badge}
                        </span>
                        )}
                    </button>
                    ))}
                </div>
              </div>

              {/* Body */}
              <div style={{ flex:1, overflow:'hidden', position:'relative' }}>
                <AnimatePresence mode="wait">

                  {/* Détail */}
                  {view === 'detail' && sel && (
                    <motion.div key="detail" initial={{opacity:0,x:16}} animate={{opacity:1,x:0}} exit={{opacity:0,x:16}} transition={{duration:.18}}
                      style={{ position:'absolute', inset:0 }}>
                      <MessageDetail msg={sel} uid={userId} onBack={back} onReply={replyToMessage} onArchive={archive} dark={dark} sent={tab==='sent'} />
                    </motion.div>
                  )}

                  {/* Inbox */}
                  {view === 'list' && tab === 'inbox' && (
                    <motion.div key="inbox" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                      style={{ position:'absolute', inset:0, overflowY:'auto', scrollbarWidth:'thin' }}>
                      {loading && <Skeleton dark={dark}/>}
                      {!loading && fInbox.length === 0 && <Empty icon={<MailOpen size={26}/>} label={q?'Aucun résultat':'Inbox vide'} dark={dark}/>}
                      <AnimatePresence>{fInbox.map(m=><MessageRow key={m._id} msg={m} uid={userId} onClick={openMsg} onArchive={archive} dark={dark}/>)}</AnimatePresence>
                    </motion.div>
                  )}

                  {/* Envoyés */}
                  {view === 'list' && tab === 'sent' && (
                    <motion.div key="sent" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                      style={{ position:'absolute', inset:0, overflowY:'auto', scrollbarWidth:'thin' }}>
                      {loading && <Skeleton dark={dark}/>}
                      {!loading && fSent.length === 0 && <Empty icon={<Send size={26}/>} label="Aucun message envoyé" dark={dark}/>}
                      <AnimatePresence>{fSent.map(m=><MessageRow key={m._id} msg={m} uid={userId} onClick={openMsg} onArchive={null} dark={dark} sent/>)}</AnimatePresence>
                    </motion.div>
                  )}

                  {/* Archivés */}
                  {view === 'list' && tab === 'archived' && (
                    <motion.div key="archived" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                      style={{ position:'absolute', inset:0, overflowY:'auto', scrollbarWidth:'thin' }}>
                      {loading && <Skeleton dark={dark}/>}
                      {!loading && inbox.length === 0 && <Empty icon={<Archive size={26}/>} label="Aucun message archivé" dark={dark}/>}
                      <AnimatePresence>{inbox.map(m=><MessageRow key={m._id} msg={m} uid={userId} onClick={openMsg} onArchive={null} dark={dark}/>)}</AnimatePresence>
                    </motion.div>
                  )}

                  {/* Notifications legacy */}
                  {view === 'list' && tab === 'notifications' && (
                    <motion.div key="notifs" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
                      style={{ position:'absolute', inset:0, overflowY:'auto', scrollbarWidth:'thin' }}>
                      {legacyUnreadCount > 0 && (
                        <div style={{ padding:'8px 16px', display:'flex', justifyContent:'flex-end', borderBottom:`1px solid ${div}` }}>
                          <button onClick={onLegacyReadAll}
                            style={{ fontSize:7,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.1em',cursor:'pointer',background:'none',border:'none',color:dark?'rgba(255,255,255,.25)':'rgba(0,0,0,.25)' }}
                            onMouseEnter={e=>e.currentTarget.style.color='#f97316'}
                            onMouseLeave={e=>e.currentTarget.style.color=dark?'rgba(255,255,255,.25)':'rgba(0,0,0,.25)'}
                          >Tout marquer lu</button>
                        </div>
                      )}
                      {legacyNotifications.length === 0
                        ? <Empty icon={<Bell size={26}/>} label="Aucune notification" dark={dark}/>
                        : legacyNotifications.sort((a,b)=>new Date(b.date)-new Date(a.date)).map((n,i)=>(
                          <LegacyRow key={n.id||i} notif={n} dark={dark} onRead={onLegacyRead}/>
                        ))
                      }
                    </motion.div>
                  )}

                </AnimatePresence>
              </div>

              {/* Footer */}
              {error && (
                <div style={{ flexShrink:0, display:'flex', alignItems:'center', gap:8, padding:'6px 16px',
                              borderTop:'1px solid rgba(239,68,68,.2)', background:'rgba(239,68,68,.06)' }}>
                  <AlertCircle size={11} color="#f87171"/>
                  <span style={{ fontSize:8,fontFamily:'monospace',color:'#f87171' }}>{error}</span>
                </div>
              )}
              <div style={{ flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between',
                            padding:'8px 16px', borderTop:`1px solid ${div}` }}>
                <span style={{ fontSize:7,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.2em',color:dark?'rgba(255,255,255,.15)':'rgba(0,0,0,.2)' }}>
                  Messagerie_v1.0 // {connected?'Live':'Offline'}
                </span>
                <span style={{ fontSize:7,fontFamily:'monospace',color:dark?'rgba(255,255,255,.15)':'rgba(0,0,0,.2)' }}>
                  {inbox.length} message{inbox.length!==1?'s':''}
                </span>
              </div>
            </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ─── utilitaires ─────────────────────────────────────────────────────────────

const Empty = ({ icon, label, dark }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', gap:16, padding:'80px 0' }}>
    <div style={{ padding:20, border:`1px solid ${dark?'rgba(255,255,255,.06)':'rgba(0,0,0,.06)'}`, color:dark?'rgba(255,255,255,.12)':'rgba(0,0,0,.12)' }}>{icon}</div>
    <span style={{ fontSize:9,fontFamily:'monospace',textTransform:'uppercase',letterSpacing:'0.12em',color:dark?'rgba(255,255,255,.25)':'rgba(0,0,0,.25)' }}>{label}</span>
  </div>
);

const Skeleton = ({ dark }) => (
  <div>
    {[...Array(6)].map((_,i)=>(
      <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'14px 16px',
                             borderBottom:`1px solid ${dark?'rgba(255,255,255,.04)':'rgba(0,0,0,.04)'}`,
                             animationDelay:`${i*80}ms` }} className="animate-pulse">
        <div style={{ width:32,height:32,flexShrink:0,background:dark?'rgba(255,255,255,.04)':'rgba(0,0,0,.04)' }}/>
        <div style={{ flex:1,display:'flex',flexDirection:'column',gap:6,paddingTop:2 }}>
          <div style={{ height:10,width:'70%',background:dark?'rgba(255,255,255,.05)':'rgba(0,0,0,.05)' }}/>
          <div style={{ height:8,background:dark?'rgba(255,255,255,.03)':'rgba(0,0,0,.03)' }}/>
          <div style={{ height:8,width:'50%',background:dark?'rgba(255,255,255,.025)':'rgba(0,0,0,.025)' }}/>
        </div>
      </div>
    ))}
  </div>
);

const LegacyRow = ({ notif, dark, onRead }) => {
  const icons = { certificate:'🏆', badge:'⭐', module_completion:'✓' };
  const div = dark?'rgba(255,255,255,.04)':'rgba(0,0,0,.04)';
  return (
    <div onClick={()=>onRead?.(notif.id)}
      style={{ display:'flex',alignItems:'flex-start',gap:12,padding:'14px 16px',cursor:'pointer',
               borderBottom:`1px solid ${div}`,
               background:!notif.read?(dark?'rgba(249,115,22,.04)':'rgba(249,115,22,.03)'):'transparent' }}
      onMouseEnter={e=>e.currentTarget.style.background=dark?'rgba(255,255,255,.025)':'rgba(0,0,0,.02)'}
      onMouseLeave={e=>e.currentTarget.style.background=!notif.read?(dark?'rgba(249,115,22,.04)':'rgba(249,115,22,.03)'):'transparent'}
    >
      <span style={{ fontSize:14,flexShrink:0,marginTop:1 }}>{icons[notif.type]||'·'}</span>
      <div style={{ flex:1,minWidth:0 }}>
        <p style={{ fontSize:11,lineHeight:1.5,fontWeight:notif.read?400:600, color:dark?'rgba(255,255,255,.75)':'rgba(0,0,0,.7)' }}>{notif.message}</p>
        <p style={{ fontSize:8,fontFamily:'monospace',marginTop:3,textTransform:'uppercase',color:dark?'rgba(255,255,255,.25)':'rgba(0,0,0,.3)' }}>{fmt(notif.date)}</p>
      </div>
      {!notif.read && <div style={{ width:6,height:6,borderRadius:9999,background:'#f97316',flexShrink:0,marginTop:6 }} className="animate-pulse"/>}
    </div>
  );
};

export { useMessagrieBadge } from '../hooks/useMessagerie';

